import { createContext, useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePrincipal } from '../principal/PrincipalContext';

const NavStateContext = createContext();

export const NavStateProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { principal, org } = usePrincipal();

  // Kept per org and user so a shared browser doesn't mix people's history
  const storageKey = `navUsage.${org}.${principal.username}`;

  const readUsage = () => JSON.parse(localStorage.getItem(storageKey) ?? '[]');

  // Identical url + state (ignoring id/url) counts as the same destination
  const saveUsage = (newState) => {
    // Object destructuring to extract the id and url, leaving the rest as state
    const { id, url, ...state } = newState;

    // Ignore the home page
    if (url === '/') return;

    const key = url + JSON.stringify(state);
    const usage = readUsage();
    const existing = usage.find((u) => u.key === key);
    if (existing) {
      existing.count++;
      existing.lastUsed = Date.now();
    } else {
      usage.push({ key, url, state, count: 1, lastUsed: Date.now() });
    }
    // Sort by count, then last used and cap at 50
    usage.sort((a, b) => b.count - a.count || b.lastUsed - a.lastUsed);
    localStorage.setItem(storageKey, JSON.stringify(usage.slice(0, 50)));
  };

  const [refreshNavState, setRefreshNavState] = useState(false); // A dummy variable used to force navState to recalculate after assign() is called

  const navStateFromStorage = () => {
    const params = new URLSearchParams(location.search);
    const navStateId = params.get('navStateId');
    if (!navStateId) return {};

    const key = `navState${navStateId}`;
    let navStateStr = sessionStorage.getItem(key);

    if (!navStateStr) {
      // Check if this is a new tab with state passed through localStorage
      navStateStr = localStorage.getItem('newTabState');
      if (navStateStr) {
        // Clean up localStorage
        sessionStorage.setItem(key, navStateStr);
        localStorage.removeItem(key);
      }
    }

    return navStateStr ? JSON.parse(navStateStr) : {};
  };

  // Whenever the navStateId changes we must refresh the navState
  const navState = useMemo(() => {
    return navStateFromStorage();
  }, [location.search, refreshNavState]);

  const saveNavState = (newState) => {
    sessionStorage.setItem('navState' + newState.id, JSON.stringify(newState));
  };

  const assign = (key, obj) => {
    if (key === 'breadcrumb') {
      console.error('The breadcrumb method must be used to update the title');
      return;
    }

    // Always update based on sessionStorage in case assign() is called twice in the same render cycle
    const prevNavState = navStateFromStorage();
    const updatedNavState = { ...prevNavState, [key]: obj };
    saveNavState(updatedNavState);

    setRefreshNavState((prev) => !prev);
  };

  const mode = navState.mode ?? 'LIST';

  // Navigation
  const navigateTo = (url, newState = {}, openNewTab = false) => {
    // Clear any future states
    if (!openNewTab) {
      let nextId = (navState.id ?? 0) + 1;
      while (sessionStorage.getItem('navState' + nextId)) {
        sessionStorage.removeItem('navState' + nextId);
        nextId++;
      }
    }

    let nextId = openNewTab ? 1 : (navState.id ?? 0) + 1;
    newState.id = nextId;
    newState.url = url;

    saveUsage(newState);

    if (!openNewTab) {
      saveNavState(newState);
      // TODO: AH discuss this
      navigate({ pathname: url, search: `?navStateId=${nextId}` }, { state: newState });
    } else {
      const href = `${url}?navStateId=${nextId}`;
      localStorage.setItem('newTabState', JSON.stringify(newState));
      window.open(href, '_blank', 'noopener');
    }
  };

  return (
    <NavStateContext.Provider value={{ navState, assign, mode, navigateTo, readUsage }}>
      {children}
    </NavStateContext.Provider>
  );
};

export const useNavState = () => useContext(NavStateContext);
